/**
 * @description 表格列配置入口
 */
// import { CommonColumnBuilders } from '@intelligence/v3/utils/columns';
import { getVisibleGenreColumns } from './genreColumns';
import { getMetricColumns } from './metricColumns';
import { getTopGamesColumns, getRisingGamesColumns } from './gamesColumns';

import type { TableColumn } from 'tea-component';
import type { IOptionWithChildren } from '@intelligence/components/GenreSelect/types';
import type { TDataSourceType, TMetricType, TDimension, TTableSegment } from '../../../types';

/**
 * 构建表格所有列配置
 * @param options 配置选项
 * @returns 表格列配置数组
 */
/**
 * 游戏信息
 */
interface IGameInfo {
  entity_name?: string;
  cover?: string;
}

export interface IBuildColumnsOptions {
  /**
   * 当前语言环境
   */
  locale: string;
  /**
   * 实体类型 (用于表格图片组件)
   */
  entityType: IEntityType;
  /**
   * 当前日期粒度
   */
  granularity?: IGranularity;
  /**
   * 品类选项数据 map
   */
  genreOptionsMap?: IRecord<IOptionWithChildren[]>;
  /** 品类数据源 */
  genreSource: IGenreSource;
  /** 当前数据源 */
  dataSource: TDataSourceType;
  /** 当前指标 */
  metric: TMetricType;
  /** 维度值 */
  dimension: TDimension;
  /** 表格展示列Segment值 */
  tableSegment: TTableSegment;
  /**
   * 排序状态列表
   */
  sorts: IRatioMetricSortState<ICasualGenreProfileMetricData>[];
  /**
   * 排序的回调
   * @param sorts 排序状态列表
   */
  onSortsChange?: (sorts: IRatioMetricSortState<ICasualGenreProfileMetricData>[]) => void;
  /**
   * 游戏信息Map (从接口返回的ext.entity_info中获取)
   */
  entityInfoMap?: IRecord<IGameInfo>;
  /**
   * More按钮点击回调
   * @param gameIds 游戏ID列表
   * @param columnKey 列key
   */
  onMoreGamesClick?: (columnKey: string, record: ICasualGenreProfileMetricData) => void;
  /**
   * 搜索关键字（用于高亮显示）
   */
  keyword?: string;
}

/**
 * 构建表格所有列配置
 */
export const buildColumns = (options: IBuildColumnsOptions): TableColumn<ICasualGenreProfileMetricData>[] => {
  const {
    locale,
    entityType,
    granularity = 'monthly',
    genreOptionsMap,
    genreSource,
    dataSource,
    metric,
    dimension,
    tableSegment,
    sorts,
    onSortsChange,
    entityInfoMap,
    onMoreGamesClick,
    keyword,
  } = options;

  const columns: TableColumn<ICasualGenreProfileMetricData>[] = [];

  // 1. 品类列（根据数据源和维度动态显示）
  const genreColumns = getVisibleGenreColumns(locale, genreOptionsMap || {}, genreSource, dimension, keyword);
  columns.push(...genreColumns);

  // 2. 指标列 / Games 列（根据 tableSegment、metric 和 granularity 动态显示）
  const metricColumns = getMetricColumns({
    locale,
    granularity,
    dataSource,
    metric,
    tableSegment,
    sorts,
    onSortsChange,
  });
  columns.push(...metricColumns);

  // 3. 构建 Games 列渲染配置
  const gamesRenderConfig = {
    entityType,
    entityInfoMap: entityInfoMap || {},
    onMoreClick: onMoreGamesClick,
  };

  const topGamesColumns = getTopGamesColumns(granularity, gamesRenderConfig);
  columns.push(...topGamesColumns);

  const risingGamesColumns = getRisingGamesColumns(granularity, gamesRenderConfig);
  columns.push(...risingGamesColumns);

  // // 4. Actions 列
  // const actionsColumn = CommonColumnBuilders.buildActionsColumn<ICasualGenreProfileMetricData>({
  //   align: 'center',
  //   onClick: () => {
  //     // 暂不处理点击逻辑
  //   },
  // });
  // columns.push(actionsColumn);

  return columns;
};

export { getVisibleGenreColumns } from './genreColumns';
export { getMetricColumns, buildMetricColumn } from './metricColumns';
export { getTopGamesColumns, getRisingGamesColumns } from './gamesColumns';
